import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

const PREFIX = "yl_tip_seen_";

// Resolve the current route to a tip. Order matters (most specific first).
function tipFor(pathname) {
  if (pathname === "/") return { key: "workspace", text: "This is your Workspace — every client engagement lives here. Tap me for a hand anytime." };
  if (pathname.startsWith("/opportunities/")) return { key: "opp", text: "Upload the tender, then move through Understand → Qualify → Proposals → Solution Design. Ask me to explain any tab." };
  if (pathname.startsWith("/opportunities")) return { key: "rfq", text: "RFQ Studio turns tenders into branded proposals. Want a walkthrough of creating your first one?" };
  if (/\/projects\/[^/]+\/meetings\/[^/]+/.test(pathname)) return { key: "meeting", text: "Record or upload audio, let the Quality Guard clean it, then generate your 18-section Minutes. Need the steps?" };
  if (pathname.startsWith("/projects/new") || pathname.endsWith("/edit")) return { key: "newproj", text: "Set the client, industry and SAP/CRM systems here — it shapes every deliverable the AI writes." };
  if (/\/projects\/[^/]+$/.test(pathname)) return { key: "hub", text: "This is the Project Hub: Kanban, Gantt, RAID log and documents. Ask me what any of these mean." };
  if (pathname.startsWith("/admin")) return { key: "admin", text: "Invite teammates and activate or deactivate users here. Want to know how invites work?" };
  if (pathname.startsWith("/settings")) return { key: "settings", text: "Tune your workspace here. I can explain any setting — just ask." };
  return null;
}

export default function ContextualTip({ suppressed, onOpenHelp }) {
  const location = useLocation();
  const [tip, setTip] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const t = tipFor(location.pathname);
    if (!t) { setTip(null); return; }
    if (localStorage.getItem(PREFIX + t.key)) { setTip(null); return; }
    setTip(t);
    const show = setTimeout(() => setVisible(true), 1400);
    return () => clearTimeout(show);
  }, [location.pathname]);

  const dismiss = () => {
    if (tip) localStorage.setItem(PREFIX + tip.key, "1");
    setVisible(false);
  };

  if (!tip || !visible || suppressed) return null;

  return (
    <div
      data-testid="contextual-tip"
      className="fixed bottom-24 right-4 lg:right-6 z-40 w-[min(320px,calc(100vw-2rem))] animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="relative rounded-2xl bg-background border border-[#29abe2]/40 shadow-xl p-3.5 pr-8">
        <button onClick={dismiss} data-testid="contextual-tip-dismiss"
          className="absolute top-2 right-2 h-6 w-6 grid place-items-center rounded-md text-muted-foreground hover:bg-secondary transition-colors">
          <X size={13} />
        </button>
        <div className="flex gap-2.5">
          <div className="h-8 w-8 shrink-0 rounded-lg brand-gradient text-white grid place-items-center">
            <Sparkles size={15} />
          </div>
          <div>
            <div className="text-xs font-semibold text-[#1a7bb0] dark:text-[#5fc3f0]">Aria tip</div>
            <p className="text-sm text-foreground mt-0.5 leading-snug">{tip.text}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { onOpenHelp?.(); dismiss(); }} data-testid="contextual-tip-ask"
                className="text-xs font-semibold text-white bg-[#29abe2] hover:bg-[#1a7bb0] rounded-full px-3 py-1 transition-colors">
                Ask Aria
              </button>
              <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground px-2">
                Got it
              </button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1.5 right-10 h-3 w-3 rotate-45 bg-background border-b border-r border-[#29abe2]/40" />
      </div>
    </div>
  );
}
